import { Button } from '@brightdrop/bd-ui';
import { Box, Typography } from '@mui/material';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  SIDEKICK_FEATURE_EVENT,
  useSidekickFeatureEvent,
} from '../../featureEvents';
import { SIDEKICK_TRANSLATION_NAMESPACE } from '../../translations';
import LockGlyph from '../icons/LockGlyph';
import { DOCK_UPSELL_CTA_WIDTH } from './constants';
import useStyles from './SidekickDock.styles';
import type { UpsellCardProps } from './types';

/**
 * Body of the expanded dock for accounts without a telematics plan.
 *
 * The assistant answers from telematics data, so there is nothing to
 * ask it about until the plan is active — the composer and chips are
 * replaced by the reason and, for users who can act on it, a route
 * into the plan catalog.
 */
function UpsellCard({
  showExplorePlansCta = false,
  onExplorePlans,
}: UpsellCardProps) {
  const { classes } = useStyles();
  const { t } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);
  const emitFeatureEvent = useSidekickFeatureEvent();
  const hasReportedViewRef = useRef(false);

  // One view per mount: the card unmounts whenever the dock leaves it.
  useEffect(() => {
    if (hasReportedViewRef.current) {
      return;
    }
    hasReportedViewRef.current = true;
    emitFeatureEvent({
      type: SIDEKICK_FEATURE_EVENT.UPSELL_VIEWED,
      hasExplorePlansCta: showExplorePlansCta,
    });
  }, [emitFeatureEvent, showExplorePlansCta]);

  const handleExplorePlans = useCallback(() => {
    emitFeatureEvent({ type: SIDEKICK_FEATURE_EVENT.EXPLORE_PLANS_CLICKED });
    onExplorePlans?.();
  }, [emitFeatureEvent, onExplorePlans]);

  return (
    <Box className={classes.upsellBody} data-testid="ai-sidekick-dock-upsell">
      <Box className={classes.upsellMessage}>
        <LockGlyph className={classes.upsellIcon} />
        <Box className={classes.upsellCopy}>
          <Typography className={classes.title} component="h2">
            {t('dock.upsell.title')}
          </Typography>
          <Typography className={classes.subtitle} component="p">
            {t('dock.upsell.body')}
          </Typography>
        </Box>
      </Box>
      {showExplorePlansCta ? (
        <Button
          variant="primary"
          size="medium"
          width={DOCK_UPSELL_CTA_WIDTH}
          onClick={handleExplorePlans}
          data-testid="ai-sidekick-dock-upsell-cta"
        >
          {t('dock.upsell.ctaLabel')}
        </Button>
      ) : null}
    </Box>
  );
}

export default UpsellCard;
